import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import { AUTH_BYPASS, loginRequest, MAJANS_TENANT_ID } from '../config/auth';

export interface AuthGateProps {
  children: ReactNode;
}

/**
 * Blocks the app until an interactive MSAL sign-in has completed.
 * `VITE_AUTH_BYPASS=true` short-circuits the whole gate for local dev.
 */
export default function AuthGate({ children }: AuthGateProps): ReactNode {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [error, setError] = useState<string | null>(null);

  // Pin the active account to the Majans tenant — users with several cached
  // Microsoft accounts otherwise land on the wrong one and see empty data.
  //
  // Deliberately NOT `?? accounts[0]`, which is what this did until 2026-08-17.
  // config/auth.ts:78 rejects exactly that fallback and explains why: an account from
  // another tenant can NEVER be issued a token for api://<clientId>, so pinning one
  // turns a fixable "you are on the wrong account" into a silent renewal that fails for
  // ever — every page reads "Re-authenticating…" and nothing says the ACCOUNT is the
  // problem. The two files disagreed, and this one won because it runs first.
  // Leaving no active account lets acquireApiToken name the signed-in accounts instead.
  useEffect(() => {
    if (AUTH_BYPASS) return;
    if (!instance.getActiveAccount() && accounts.length > 0) {
      // The `| undefined` is the point, not an oversight: "no Majans account among the
      // cached ones" is a state this component must leave alone, and the annotation says
      // so out loud.
      //
      // It is NOT a compiler guarantee, and do not read it as one — `= accounts.find(…)
      // ?? accounts[0]` type-checks perfectly happily under this repo's own --strict
      // (verified by compiling that exact line, 2026-08-18), because `accounts[0]` is
      // just an `AccountInfo`. What stops the regression is the comment above and
      // config/auth.ts:78, not the type. A guarantee the compiler does not actually give
      // is worse than none, because the next reader trusts it and stops checking.
      const majans: AccountInfo | undefined = accounts.find((a) => a.tenantId === MAJANS_TENANT_ID);
      if (majans) instance.setActiveAccount(majans);
    }
  }, [instance, accounts]);

  if (AUTH_BYPASS) return children;

  if (isAuthenticated) return children;

  const signIn = async () => {
    setError(null);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      // `catch` binds `unknown`. Asserted rather than narrowed so the expression below
      // is byte-for-byte the one that shipped — `?.` and `??` already carry the "no
      // usable message" case, and a `typeof` narrowing here would change what a
      // non-string message renders as.
      const err = e as { message?: string } | null | undefined;
      setError(err?.message ?? 'Sign-in failed');
    }
  };

  return (
    <div className="auth-gate">
      <div className="auth-card">
        <img src="/majans-logo.png" alt="" className="auth-logo" />
        <h1>Majans Purchasing</h1>
        <p className="muted">Supplier intelligence, price defense and sourcing.</p>
        {error && <p className="auth-error">{error}</p>}
        <button className="btn btn-primary" onClick={signIn} disabled={inProgress !== 'none'}>
          {inProgress !== 'none' ? 'Signing in…' : 'Sign in with Microsoft'}
        </button>
      </div>
    </div>
  );
}
